/**
 * Streak Display Component
 * Shows user's current reading streak with fire animation
 */

import React from 'react';

interface StreakDisplayProps {
  currentStreak: number;
  longestStreak?: number;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const StreakDisplay: React.FC<StreakDisplayProps> = ({
  currentStreak,
  longestStreak,
  showDetails = true,
  size = 'md',
}) => {
  const sizeClasses = {
    sm: { icon: 'text-2xl', text: 'text-sm', subtext: 'text-xs' },
    md: { icon: 'text-3xl', text: 'text-lg', subtext: 'text-sm' },
    lg: { icon: 'text-4xl', text: 'text-2xl', subtext: 'text-base' },
  };

  const sizes = sizeClasses[size];
  const isOnFire = currentStreak >= 7;
  const isMilestone = currentStreak >= 30;

  // Determine streak color
  const getStreakColor = () => {
    if (currentStreak === 0) return 'text-slate-400';
    if (currentStreak < 3) return 'text-orange-400';
    if (currentStreak < 7) return 'text-orange-500';
    if (currentStreak < 14) return 'text-orange-500';
    if (currentStreak < 30) return 'text-red-500';
    if (currentStreak < 100) return 'text-red-600';
    return 'text-amber-500';
  };

  return (
    <div className="flex items-center gap-3">
      {/* Fire Icon */}
      <div className={`${sizes.icon} relative`}>
        <span className={getStreakColor()}>
          {currentStreak === 0 && '💤'}
          {currentStreak > 0 && currentStreak < 7 && '🔥'}
          {currentStreak >= 7 && currentStreak < 30 && '🔥'}
          {currentStreak >= 30 && '🌟'}
        </span>
        {isOnFire && (
          <span className="absolute -top-1 -right-1 animate-pulse text-xs">
            ✨
          </span>
        )}
      </div>

      {/* Streak Count */}
      <div>
        <div className={`${sizes.text} font-bold ${getStreakColor()}`}>
          {currentStreak} day{currentStreak !== 1 ? 's' : ''}
        </div>
        {showDetails && (
          <div className={`${sizes.subtext} text-slate-500 dark:text-slate-400`}>
            {currentStreak === 0 && 'Start reading to build your streak!'}
            {currentStreak > 0 && currentStreak < 7 && 'Building momentum...'}
            {currentStreak >= 7 && currentStreak < 14 && 'Week warrior! Keep it up!'}
            {currentStreak >= 14 && currentStreak < 30 && 'Incredible dedication!'}
            {currentStreak >= 30 && 'Legendary streak!'}
          </div>
        )}
      </div>

      {/* Longest Streak (optional) */}
      {showDetails && longestStreak !== undefined && longestStreak > 0 && (
        <div className="ml-auto text-right">
          <div className={`${sizes.subtext} text-slate-500 dark:text-slate-400`}>
            Best
          </div>
          <div className={`${sizes.subtext} font-semibold text-amber-500`}>
            {longestStreak} days
          </div>
        </div>
      )}
    </div>
  );
};

export default StreakDisplay;
